import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Form } from 'react-router-dom';

import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import { useCreateIdeaMutation, useUpdateIdeaMutation } from '../../../../../store/services/ideasApi';
import { CreateIdeaPayload, UpdateIdeaPayload, Idea } from '../../../../../types/idea.types';

import styles from './CreateIdeaModal.module.scss';

type FormikValues = {
  name: string;
};

type CreateIdeaModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (created?: Idea, editedName?: string) => void | Promise<void>;
  isEditMode?: boolean;
  ideaData?: Idea;
};

const CreateIdeaModal: FC<CreateIdeaModalProps> = ({ open, onClose, onSuccess, isEditMode = false, ideaData }) => {
  const [createIdea, { isLoading: isCreating }] = useCreateIdeaMutation();
  const [updateIdea, { isLoading: isUpdating }] = useUpdateIdeaMutation();

  const [resErrors, setResErrors] = useState<string | null>(null);

  const isLoading = isCreating || isUpdating;

  const getInitialValues = (): FormikValues => {
    if (isEditMode && ideaData) {
      return {
        name: ideaData.name,
      };
    }
    return {
      name: '',
    };
  };

  const initialValues: FormikValues = getInitialValues();

  const handleSubmit = async (values: FormikValues) => {
    try {
      if (isEditMode && ideaData) {
        const payload: UpdateIdeaPayload = {
          name: values.name,
        };

        console.log('Updating idea with payload:', payload);
        await updateIdea({ id: ideaData.id, data: payload }).unwrap();
        setResErrors(null);
        await Promise.resolve(onSuccess?.(undefined, values.name.trim()));
      } else {
        const payload: CreateIdeaPayload = {
          name: values.name,
        };

        console.log('Creating idea with payload:', payload);
        const created = await createIdea(payload).unwrap();
        setResErrors(null);
        await Promise.resolve(onSuccess?.(created));
      }

      onClose();
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} idea:`, error);
      setResErrors(error?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} idea`);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: Partial<FormikValues> = {};

    if (!values.name.trim()) {
      errors.name = 'Idea name is required';
    }

    return errors;
  };

  const handleClose = () => {
    onClose();
    setResErrors(null);
  };

  const getModalTitle = () => {
    return isEditMode ? 'Edit Idea' : 'New Idea';
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      return isEditMode ? 'Updating...' : 'Creating...';
    }
    return isEditMode ? 'Update' : 'Create';
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby={isEditMode ? 'edit-idea-modal' : 'create-idea-modal'}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '545px',
          minWidth: 545,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '20px',
          p: '32px 64px',
          overflowY: 'auto',
          boxShadow: 'none',
          maxHeight: '90vh',
          '@media (max-width: 900px)': {
            p: '16px 32px',
            minWidth: 300,
            maxWidth: '90vw',
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
          <CloseIcon
            sx={{ width: '30px', height: '30px' }}
            onClick={handleClose}
          />
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h5Bold"
            sx={{ textAlign: 'center', mb: 3 }}
            color="#222B3A"
          >
            {getModalTitle()}
          </Typography>

          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, handleChange, handleSubmit, errors, touched }) => (
              <Form
                className={styles.ideaForm}
                onSubmit={handleSubmit}
              >
                <Box className={styles.formSection}>
                  <CustomInput
                    inputName="name"
                    inputLabel="Name"
                    placeholder="Enter idea name"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && errors.name ? errors.name : ''}
                  />
                </Box>

                <Box className={styles.modalActions}>
                  <CustomButton
                    btnType="button"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    disabled={isLoading || !values.name.trim()}
                  >
                    {getSubmitButtonText()}
                  </CustomButton>
                </Box>

                {resErrors && (
                  <Typography
                    variant="h12"
                    sx={{ color: '#C8102E', textAlign: 'center', mt: 2 }}
                  >
                    {resErrors}
                  </Typography>
                )}
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateIdeaModal;
